/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import { Button, ButtonGroup, Modal } from '@ver-uds/react';
import CSVFileValidator from 'csv-file-validator';
import { FileInputControl, Form } from '@ver-uds/uswds-react';
import { FormProvider, useForm } from 'react-hook-form';
import { CSVLink } from 'react-csv';
import { HiringSite, State } from 'eve-account-client';
import { Link } from 'react-router-dom';
import ErrorConstants from '../../../constants/ErrorConstants';
import { InternalRoutes } from '../../../constants';
import { ValidationResult, HiringSiteValidator } from './HiringSiteValidator';

export type BulkUploadModalProps = {
  open: boolean;
  close(): void;
  validStates: State[];
  existingSites: HiringSite[];
  onSubmit: (validationResult: ValidationResult) => void;
};

type FormValues = {
  csvFile: FileList;
};

const BulkUploadModal = ({ open, close, validStates, existingSites, onSubmit }: BulkUploadModalProps): JSX.Element => {
  const [validationResult, setValidationResult] = useState<ValidationResult>();
  const [fileErrorMessage, setFileErrorMessage] = useState<string>();
  const modalId = 'bulk-upload-modal';
  const formId = `${modalId}-form`;

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const formMethods = useForm<FormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { register, handleSubmit } = formMethods;

  const handleSubmitForm = handleSubmit(() => {
    if (validationResult) {
      onSubmit(validationResult);
    }
  });

  const handeOnClose = () => {
    setFileErrorMessage(undefined);
    close();
  };

  const csvTemplateData = 'Street Address,Suite/Other,City,State,Zip Code';
  return (
    <Modal id={modalId} header="Bulk Upload Hiring Sites" open={open} close={close} fixedToTop>
      <FormProvider {...formMethods}>
        <Form id={formId} className="maxw-none margin-y-4" onSubmit={handleSubmitForm} noValidate>
          <Modal.Content>
            <div>
              Make sure your CSV column headers include: <b>Street Address</b>, <b>Suite/Other</b>, <b>City</b>,&nbsp;
              <b>State</b>, and&nbsp;<b>Zip Code</b> to help ensure a successful upload.
            </div>

            <CSVLink
              data={csvTemplateData}
              filename="Hiring Sites Template"
              style={{ textDecoration: 'none' }}
              target="_blank"
            >
              <Button type="button" variant="secondary" className="margin-bottom-5 margin-right-3">
                Download CSV template
              </Button>
            </CSVLink>
            <Link
              to={InternalRoutes.CSV_GUIDELINES}
              rel="noopener noreferrer"
              target="_blank"
              className="usa-button usa-button--unstyled ver-button"
              aria-label="View Bulk Upload CSV Guidelines in a new tab"
            >
              View CSV Guidelines
            </Link>

            <FileInputControl
              id="bulk-upload-csv"
              name="csvFile"
              className="maxw-none"
              required
              label="Select a CSV file"
              hint="Must be a CSV"
              accept=".csv"
              errorMessage={fileErrorMessage}
              ref={register({
                validate: (fileList: FileList) => {
                  setFileErrorMessage(undefined);
                  const parseFile = (file: File) => {
                    const CSVConfig = {
                      headers: [
                        { name: 'Street Address', inputName: 'line1' },
                        { name: 'Suite/Other', inputName: 'line2' },
                        { name: 'City', inputName: 'city' },
                        { name: 'State', inputName: 'stateCode' },
                        { name: 'Zip Code', inputName: 'zipCode' },
                      ],
                    };

                    CSVFileValidator(file, CSVConfig)
                      .then((csvData) => {
                        if (csvData.data.length > 1000) {
                          setFileErrorMessage(ErrorConstants.FILE_EXCEED_1000_ROWS);
                          return;
                        }

                        if (csvData.inValidData.length > 0) {
                          setFileErrorMessage(ErrorConstants.CSV_FORMAT_ERROR);
                        }
                        const results = new HiringSiteValidator(validStates, existingSites).validate(
                          csvData.data as HiringSite[],
                        );
                        setValidationResult(results);
                      })
                      .catch(() => 'obligatory catch');
                  };
                  if (fileList.length === 0) {
                    setFileErrorMessage(ErrorConstants.SELECT_CSV_FILE);
                    return false;
                  }
                  const file = fileList.item(0) as File;
                  if (file.type !== 'text/csv') {
                    return false;
                  }
                  if (file.size / (1024 * 1024) > 1) {
                    setFileErrorMessage(ErrorConstants.FILE_SIZE_EXCEED_1MB);
                    return false;
                  }
                  parseFile(file);

                  if (fileErrorMessage) {
                    return false;
                  }
                  return true;
                },
              })}
            />
          </Modal.Content>
          <Modal.Footer>
            <ButtonGroup>
              <Button data-testid="bulk-upload-cancel" type="button" variant="secondary" onClick={handeOnClose}>
                Cancel
              </Button>
              <Button variant="submit">Bulk Upload Hiring Sites</Button>
            </ButtonGroup>
          </Modal.Footer>
        </Form>
      </FormProvider>
    </Modal>
  );
};

// istanbul ignore if: Not used for test environment; Modal.setAppElement should be called in the tests directly.
if (process.env.NODE_ENV !== 'test') {
  Modal.setAppElement('#root');
}

export default BulkUploadModal;
